import { toCanvas } from 'html-to-image';
import type { ReportPdfPart } from './workbookReportPdf';

/** Sharp enough to print: two device pixels per CSS pixel. */
const PDF_PIXEL_RATIO = 2;
/** The email's inline image: sharp on a high-density screen, small enough to send. */
const EMAIL_PIXEL_RATIO = 1.5;
const JPEG_QUALITY = 0.92;

/**
 * Resolves once `root` has stopped changing for `quietMs` and nothing is
 * `busy` (rows still loading), or after `timeoutMs` regardless: charts render
 * asynchronously (lazy chunks, measured sizes, vega's embed), so a quiet DOM
 * is the one signal every chart gives.
 */
export function waitForQuiet(
  root: Element,
  busy: () => boolean,
  { quietMs = 600, timeoutMs = 20_000 } = {},
): Promise<void> {
  return new Promise((resolve) => {
    const started = performance.now();
    let changed = started;
    const observer = new MutationObserver(() => {
      changed = performance.now();
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
    });
    function check() {
      const now = performance.now();
      if ((!busy() && now - changed >= quietMs) || now - started >= timeoutMs) {
        observer.disconnect();
        resolve();
      } else setTimeout(check, 100);
    }
    check();
  });
}

/**
 * Draws an element on its page's background: a part's own is transparent,
 * which a JPEG would turn black.
 */
function capture(element: HTMLElement, pixelRatio: number, background: string) {
  return toCanvas(element, {
    pixelRatio,
    backgroundColor: background,
    // The print render sits off screen; its image must not.
    style: { position: 'static', left: '0' },
    // Charts' own error icons may fail to load; one must not fail the capture.
    onImageErrorHandler: () => undefined,
  });
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('The report could not be drawn.')),
      type,
      quality,
    ),
  );
}

/** Each part as a JPEG for the PDF; only its aspect ratio sizes it on the page. */
export async function captureReportParts(
  parts: readonly HTMLElement[],
  background: string,
): Promise<ReportPdfPart[]> {
  const captured: ReportPdfPart[] = [];
  // One at a time: each capture clones its part and every style it uses.
  for (const part of parts) {
    const canvas = await capture(part, PDF_PIXEL_RATIO, background);
    const blob = await canvasBlob(canvas, 'image/jpeg', JPEG_QUALITY);
    captured.push({
      jpeg: new Uint8Array(await blob.arrayBuffer()),
      width: canvas.width,
      height: canvas.height,
    });
  }
  return captured;
}

/** The whole Report as one PNG, base64, for the email's body. */
export async function captureReportPng(root: HTMLElement): Promise<string> {
  const canvas = await capture(
    root,
    EMAIL_PIXEL_RATIO,
    getComputedStyle(root).backgroundColor,
  );
  return bytesToBase64(
    new Uint8Array(await (await canvasBlob(canvas, 'image/png')).arrayBuffer()),
  );
}

/** Base64 of bytes, in chunks so a large PDF does not overflow the call stack. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let at = 0; at < bytes.length; at += chunk)
    binary += String.fromCharCode(...bytes.subarray(at, at + chunk));
  return btoa(binary);
}
